import { useState, useEffect, useMemo } from 'react';
import { fetchDocumentContent } from '../services/googleSheets';
import { useConfig } from '../config/ConfigContext';

/**
 * DetailedNotes component - Premium "Scholarly Manuscript" design
 * Displays detailed notes with section-aware parsing and elegant styling
 * Falls back to iframe/document viewer if parsing fails
 * @param {Object} props - Component props
 * @param {string} props.url - URL to the detailed notes document
 * @param {string} props.speaker - Speaker name for download filename
 * @param {string} props.series - Series name for download filename
 * @param {string} props.date - Lecture date for download filename
 * @returns {JSX.Element} DetailedNotes component
 */
const DetailedNotes = ({ url, speaker, series, date }) => {
  const { theme } = useConfig();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // M73: Generate user-friendly download filename
  const generateDownloadFilename = () => {
    // Format: Speaker_Series_YYYY-MM-DD_Notes.pdf
    const cleanText = (text) => text?.replace(/[^a-zA-Z0-9]/g, '') || '';
    const speakerClean = cleanText(speaker) || 'Lecture';
    const seriesClean = cleanText(series) || 'Notes';

    // Format date as YYYY-MM-DD
    let dateStr = '';
    if (date) {
      const d = new Date(date);
      if (!isNaN(d.getTime())) {
        dateStr = `_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }

    return `${speakerClean}_${seriesClean}${dateStr}_Notes.pdf`;
  };

  const downloadFilename = generateDownloadFilename();

  // M73: Download PDF with custom filename (bypasses cross-origin limitation)
  const handleDownloadPdf = async () => {
    const docId = extractGoogleDocId(url);
    if (!docId) return;

    setIsDownloading(true);
    try {
      const exportUrl = `https://docs.google.com/document/d/${docId}/export?format=pdf`;
      const response = await fetch(exportUrl);

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open in new tab
      window.open(`https://docs.google.com/document/d/${docId}/export?format=pdf`, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  // Design tokens matching KeyTakeaways
  const primaryColor = theme.colors.primary || '#1e4d8b';
  const accentGold = '#c9a227';
  const lightBg = '#fafbfc';
  const cardBg = '#ffffff';

  // ═══════════════════════════════════════════════════════════════════════════════
  // M60 SCHEMA CONTRACT - Section Configuration
  // This configuration MUST match the M60 schema defined in tasks.py
  // ═══════════════════════════════════════════════════════════════════════════════
  const sectionConfig = {
    // M60 Section 1: Overview
    'overview': { icon: '📖', color: primaryColor, label: 'Overview' },

    // M60 Section 2: Key Questions Addressed (what the LECTURE answers)
    'key questions addressed': { icon: '❓', color: '#7c3aed', label: 'Key Questions Addressed' },
    'key questions': { icon: '❓', color: '#7c3aed', label: 'Key Questions Addressed' }, // Legacy support

    // M60 Section 3: Core Concepts (terms with definitions)
    'core concepts': { icon: '💡', color: '#059669', label: 'Core Concepts' },

    // M60 Section 4: Main Teachings
    'main teachings': { icon: '📚', color: primaryColor, label: 'Main Teachings' },

    // M60 Section 5: Quranic References (QURAN ONLY - separate from Hadith!)
    'quranic references': { icon: '📖', color: accentGold, label: 'Quranic References' },
    'quranic': { icon: '📖', color: accentGold, label: 'Quranic References' }, // Legacy

    // M60 Section 6: Hadith References (HADITH ONLY - separate from Quran!)
    'hadith references': { icon: '📜', color: '#059669', label: 'Hadith References' },
    'hadith': { icon: '📜', color: '#059669', label: 'Hadith References' }, // Legacy

    // Legacy combined references (for backward compatibility)
    'references': { icon: '📜', color: accentGold, label: 'References' },

    // M60 Section 7: Practical Applications
    'practical applications': { icon: '✅', color: '#0891b2', label: 'Practical Applications' },
    'practical': { icon: '✅', color: '#0891b2', label: 'Practical Applications' }, // Legacy
    'applications': { icon: '✅', color: '#0891b2', label: 'Practical Applications' },

    // M60 Section 8: Reflection Questions (for READER contemplation - different from Key Questions!)
    'reflection questions': { icon: '🤔', color: '#6366f1', label: 'Reflection Questions' },
    'reflection': { icon: '🤔', color: '#6366f1', label: 'Reflection Questions' }, // Legacy

    // M60 Section 9: Notable Quotes
    'notable quotes': { icon: '💬', color: '#be185d', label: 'Notable Quotes' },
    'quotes': { icon: '💬', color: '#be185d', label: 'Notable Quotes' }, // Legacy
    'notable': { icon: '💬', color: '#be185d', label: 'Notable Quotes' },

    'default': { icon: '📝', color: primaryColor, label: 'Notes' }
  };

  // Extract Google Doc ID from URL
  const extractGoogleDocId = (url) => {
    if (!url) return null;

    const docsMatch = url.match(/docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/);
    if (docsMatch && docsMatch[1]) return docsMatch[1];

    const driveMatch = url.match(/drive\.google\.com\/(file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/);
    if (driveMatch && driveMatch[2]) return driveMatch[2];

    if (/^[a-zA-Z0-9_-]{25,}$/.test(url)) return url;

    return null;
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // M60 SCHEMA CONTRACT - Section Detection Patterns
  // Priority: M60 exact headers first, then legacy patterns for backward compatibility
  // ═══════════════════════════════════════════════════════════════════════════════
  const sectionPatterns = [
    // ═══ M60 EXACT HEADERS (highest priority) ═══

    // M60 Section 1: Overview
    { pattern: /^overview:?$/i, type: 'overview' },

    // M60 Section 2: Key Questions Addressed (NOT same as Reflection Questions!)
    { pattern: /^key\s*questions\s*addressed:?$/i, type: 'key questions addressed' },

    // M60 Section 3: Core Concepts
    { pattern: /^core\s*concepts?:?$/i, type: 'core concepts' },

    // M60 Section 4: Main Teachings
    { pattern: /^main\s*teachings?:?$/i, type: 'main teachings' },

    // M60 Section 5: Quranic References (QURAN ONLY!)
    { pattern: /^quranic\s*references?:?$/i, type: 'quranic references' },

    // M60 Section 6: Hadith References (HADITH ONLY!)
    { pattern: /^hadith\s*references?:?$/i, type: 'hadith references' },

    // M60 Section 7: Practical Applications
    { pattern: /^practical\s*applications?:?$/i, type: 'practical applications' },

    // M60 Section 8: Reflection Questions (for READER, NOT same as Key Questions!)
    { pattern: /^reflection\s*questions?:?$/i, type: 'reflection questions' },

    // M60 Section 9: Notable Quotes
    { pattern: /^notable\s*quotes?:?$/i, type: 'notable quotes' },

    // ═══ LEGACY PATTERNS (backward compatibility) ═══

    // Legacy: Key Questions (maps to M60 "Key Questions Addressed")
    { pattern: /^(key\s*)?questions?\s*(explored|covered|to\s*consider)?:?$/i, type: 'key questions' },
    { pattern: /^discussion\s*(points?|questions?)?:?$/i, type: 'key questions' },

    // Legacy: Reflection (maps to M60 "Reflection Questions")
    { pattern: /^reflection:?$/i, type: 'reflection' },

    // Legacy: Concepts/Terms
    { pattern: /^(core\s*)?(terms?|definitions?):?$/i, type: 'core concepts' },
    { pattern: /^key\s*(terms?|vocabulary):?$/i, type: 'core concepts' },

    // Legacy: Teachings/Lessons
    { pattern: /^(main\s*)?(lessons?|points?):?$/i, type: 'main teachings' },
    { pattern: /^(the\s+)?exegesis/i, type: 'main teachings' },

    // Legacy: Combined Quran & Hadith (should be split in M60, but support old content)
    { pattern: /^quranic\s*(&|and)?\s*hadith\s*references?:?$/i, type: 'references' },  // M63: Made "and/&" optional
    { pattern: /^references?:?$/i, type: 'references' },
    { pattern: /^sources?:?$/i, type: 'references' },

    // Legacy: Applications
    { pattern: /^(practical\s*)?(takeaways?|action\s*items?):?$/i, type: 'practical' },
    { pattern: /^applications?:?$/i, type: 'applications' },

    // Legacy: Quotes
    { pattern: /^quotes?:?$/i, type: 'quotes' },
    { pattern: /^sayings?:?$/i, type: 'quotes' },

    // Legacy: Overview variants
    { pattern: /^(introduction|summary|background):?$/i, type: 'overview' },
  ];

  // PRINCIPLE: Filter out noise/metadata that doesn't belong in notes
  // This uses semantic patterns to identify non-content elements
  const isMetadataContent = (text) => {
    if (!text) return true; // Empty is metadata
    const trimmed = text.trim();
    if (trimmed.length < 3) return true; // Too short to be meaningful
    if (trimmed.length > 300) return false; // Long content is likely real content

    const lowerText = trimmed.toLowerCase();

    // PATTERN 1: Date patterns (standalone dates are metadata)
    if (/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{1,2},?\s+\d{2,4}$/i.test(trimmed)) return true;
    if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(trimmed)) return true;

    // PATTERN 2: Attribution patterns (author lines)
    if (/^by\s+(sheikh|imam|dr\.?|maulana|ayatollah)/i.test(lowerText)) return true;
    if (/^sheikh\s+[\w\s-]+\s*\|?\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(trimmed)) return true;

    // PATTERN 3: Document structure noise
    if (/^(lecture\s*notes?|page\s*\d*\s*(of)?|\d+\s*(of)?\s*\d*|table\s+of\s+contents?)$/i.test(lowerText)) return true;
    if (/^page\s*$/i.test(lowerText)) return true;

    // PATTERN 4: Repetitive short labels (often header/footer noise)
    if (trimmed.length < 30 && /^(jummah\s*khutba?|friday\s*sermon|weekly\s*lecture)$/i.test(lowerText)) return true;

    // PATTERN 5: Pure punctuation or numbers
    if (/^[\d\s\.\-\/\|:]+$/.test(trimmed)) return true;

    return false;
  };

  // PRINCIPLE: Clean HTML content by removing metadata noise embedded within elements
  // Google Docs exports often have metadata interspersed with line breaks
  const cleanMetadataFromHtml = (html) => {
    if (!html) return html;

    // Create a temporary element to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Patterns for inline metadata to remove
    const metadataPatterns = [
      /^lecture\s*notes?\s*$/i,
      /^jummah\s*khutba?\s*$/i,
      /^by\s+[\w\s.-]+$/i,                                                           // "by Name" (any attribution, not just "sheikh")
      /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{1,2},?\s+\d{2,4}$/i,
      /^[\w\s.-]+\|\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,            // "Name | Month DD" (speaker + date)
      /^sheikh\s+[\w\s-]+\s*\|/i,
      /^page\s*\d*\s*(of\s*\d*)?$/i,
    ];

    // Walk through text nodes and remove metadata
    const walker = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT, null, false);
    const nodesToRemove = [];

    // PASS 1: Count occurrences of short text nodes (for duplicate detection)
    const textCounts = {};
    while (walker.nextNode()) {
      const text = walker.currentNode.textContent?.trim() || '';
      if (text.length > 3 && text.length < 80) {
        textCounts[text] = (textCounts[text] || 0) + 1;
      }
    }

    // PASS 2: Mark nodes for removal (pattern matches + duplicated short texts)
    // Reset walker to start from beginning
    const walker2 = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT, null, false);
    while (walker2.nextNode()) {
      const node = walker2.currentNode;
      const text = node.textContent?.trim() || '';

      if (text.length > 0 && text.length < 80) {
        // Check metadata patterns
        let isMetadata = false;
        if (text.length < 60) {
          for (const pattern of metadataPatterns) {
            if (pattern.test(text)) {
              isMetadata = true;
              break;
            }
          }
        }

        // PRINCIPLE: Short text that appears more than once is metadata/header noise
        // (series titles, speaker names repeated in doc headers)
        if (!isMetadata && textCounts[text] > 1) {
          isMetadata = true;
        }

        if (isMetadata) {
          nodesToRemove.push(node);
        }
      }
    }

    // Remove identified metadata nodes
    nodesToRemove.forEach(node => {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });

    // Clean up excessive whitespace and line breaks
    let cleanedHtml = temp.innerHTML
      // Remove all leading whitespace and line breaks
      .replace(/^(\s|<br\s*\/?>|&nbsp;)+/gi, '')
      // Remove all trailing whitespace and line breaks
      .replace(/(\s|<br\s*\/?>|&nbsp;)+$/gi, '')
      // Collapse multiple consecutive breaks to max 2
      .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
      // Remove breaks immediately after opening tags
      .replace(/>(\s*<br\s*\/?>\s*)+/gi, '>')
      // Remove breaks immediately before closing tags
      .replace(/(\s*<br\s*\/?>\s*)+</gi, '<')
      // Clean up empty paragraphs
      .replace(/<p[^>]*>\s*<\/p>/gi, '')
      // Clean up multiple spaces
      .replace(/\s{2,}/g, ' ');

    return cleanedHtml.trim();
  };

  // PRINCIPLE: Detect pagination/footer artifacts that indicate document boundaries
  const isPaginationArtifact = (text) => {
    if (!text) return false;
    const trimmed = text.trim().toLowerCase();
    // "Page X of Y", "1 of 5", etc.
    if (/^page\s*\d*\s*(of\s*\d*)?$/.test(trimmed)) return true;
    if (/^\d+\s*of\s*\d+$/.test(trimmed)) return true;
    return false;
  };

  // Parse content into structured sections
  const parseNotesContent = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== 'string') return null;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const body = doc.body;

      if (!body) return null;

      const sections = [];
      let currentSection = null;
      let currentContent = [];

      // Helper to identify section type from heading text
      const getSectionType = (text) => {
        const cleanText = text.replace(/[:\-–—]/g, '').trim().toLowerCase();

        // Check against known patterns (PREFERRED - regex patterns are precise)
        for (const { pattern, type } of sectionPatterns) {
          if (pattern.test(cleanText)) {
            return { type, ...sectionConfig[type] || sectionConfig.default };
          }
        }

        // M67: STRICT FALLBACK - Only use includes() for multi-word keys
        // Single-word keys like 'hadith', 'quranic', 'reflection' are too broad
        for (const [key, config] of Object.entries(sectionConfig)) {
          if (key === 'default') continue;
          const keyWords = key.split(/\s+/).length;
          if (keyWords >= 2) {
            // Multi-word keys - includes() is safe
            if (cleanText.includes(key)) {
              return { type: key, ...config };
            }
          } else {
            // Single-word keys - require near-exact match
            if (cleanText === key || cleanText === key + 's' || cleanText === key + 'es') {
              return { type: key, ...config };
            }
          }
        }
        return { type: 'default', ...sectionConfig.default, label: text.replace(/[:\-–—]/g, '').trim() };
      };

      // Helper to extract text content
      const getTextContent = (element) => {
        return element.textContent?.trim() || '';
      };

      // Helper to check if text looks like a section header
      const looksLikeSectionHeader = (text) => {
        if (!text || text.length < 3 || text.length > 120) return false;
        const cleanText = text.replace(/[:\-–—]/g, '').trim().toLowerCase();

        // Check against known patterns (PREFERRED - regex patterns are precise)
        for (const { pattern } of sectionPatterns) {
          if (pattern.test(cleanText)) return true;
        }

        // M67: STRICT FALLBACK - Only use includes() for multi-word keys
        // Single-word keys like 'hadith', 'quranic', 'reflection' are too broad
        // and incorrectly match content like "Hadith of the Prophet"
        for (const key of Object.keys(sectionConfig)) {
          if (key === 'default') continue;
          const keyWords = key.split(/\s+/).length;
          if (keyWords >= 2) {
            // Multi-word keys (e.g., 'hadith references') - includes() is safe
            if (cleanText.includes(key)) return true;
          } else {
            // Single-word keys - require near-exact match (allow trailing s/es)
            if (cleanText === key || cleanText === key + 's' || cleanText === key + 'es') return true;
          }
        }
        return false;
      };

      // Helper to extract bold text from element (checks for section headers in bold)
      const extractBoldSectionHeader = (element) => {
        // Check for <strong> or <b> at the beginning of the element
        const strongEl = element.querySelector('strong, b');
        if (strongEl) {
          const boldText = strongEl.textContent?.trim() || '';
          if (looksLikeSectionHeader(boldText)) {
            return boldText;
          }
        }

        // Check if the element's HTML starts with bold text
        const html = element.innerHTML || '';
        const boldMatch = html.match(/^<(?:strong|b)[^>]*>([^<]+)<\/(?:strong|b)>/i);
        if (boldMatch && boldMatch[1]) {
          const boldText = boldMatch[1].trim();
          if (looksLikeSectionHeader(boldText)) {
            return boldText;
          }
        }

        // Check for span with font-weight bold at start
        const boldSpanMatch = html.match(/^<span[^>]*font-weight:\s*(?:bold|[6-9]00)[^>]*>([^<]+)<\/span>/i);
        if (boldSpanMatch && boldSpanMatch[1]) {
          const boldText = boldSpanMatch[1].trim();
          if (looksLikeSectionHeader(boldText)) {
            return boldText;
          }
        }

        return null;
      };

      // Helper to remove the header text from content
      const removeHeaderFromContent = (element, headerText) => {
        let html = element.innerHTML || '';

        // Try to remove the bold header pattern from the HTML
        const patterns = [
          new RegExp(`^<(?:strong|b)[^>]*>${headerText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:?\\s*<\\/(?:strong|b)>\\s*`, 'i'),
          new RegExp(`^<span[^>]*>${headerText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:?\\s*<\\/span>\\s*`, 'i'),
        ];

        for (const pattern of patterns) {
          if (pattern.test(html)) {
            html = html.replace(pattern, '').trim();
            break;
          }
        }

        return html;
      };

      // Track seen content to prevent duplicates (principle: process once)
      const seenContent = new Set();
      let hitPagination = false; // Stop processing after pagination artifacts

      // Process all elements - enhanced to detect bold section headers
      const processElements = (elements) => {
        elements.forEach(element => {
          // Stop processing if we've hit pagination (footer content follows)
          if (hitPagination) return;

          const tagName = element.tagName?.toLowerCase() || '';
          const className = element.className || '';
          const text = getTextContent(element);

          // Skip empty elements
          if (!text && !element.querySelector('img')) return;

          // PRINCIPLE: Filter metadata noise before processing
          if (isMetadataContent(text)) {
            console.log('[DetailedNotes] Skipping metadata:', text.substring(0, 50));
            return;
          }

          // PRINCIPLE: Detect pagination to stop processing duplicates
          if (isPaginationArtifact(text)) {
            console.log('[DetailedNotes] Hit pagination artifact, stopping:', text);
            hitPagination = true;
            return;
          }

          // M65: ENHANCED DEDUPLICATION - Avoid processing duplicate content
          // Use full text hash (up to 500 chars) for more accurate deduplication
          const normalizedText = text.toLowerCase().replace(/\s+/g, ' ').trim();
          const contentHash = normalizedText.substring(0, 500);

          // Check for exact duplicate
          if (seenContent.has(contentHash)) {
            console.log('[DetailedNotes] Skipping duplicate:', text.substring(0, 50));
            return;
          }

          // M68: BIDIRECTIONAL CONTAINMENT CHECK - Skip content that overlaps with already-seen content
          // Case 1: NEW text contains OLD text (superset) - e.g., "A B C D" contains "B C"
          // Case 2: OLD text contains NEW text (subset) - e.g., nested span "B C" is part of paragraph "A B C D"
          // Both cases indicate duplicate content that should be skipped
          const isOverlappingContent = Array.from(seenContent).some(seen => {
            const seenNormalized = seen.toLowerCase().replace(/\s+/g, ' ');
            // Skip very short texts for containment check (would match too broadly)
            if (seenNormalized.length < 30 || normalizedText.length < 30) return false;

            // Case 1: NEW contains OLD (superset - concatenated content)
            if (normalizedText.length > seenNormalized.length && normalizedText.includes(seenNormalized)) {
              console.log('[DetailedNotes] M68 Case 1 - New contains old:', normalizedText.substring(0, 40), '⊃', seenNormalized.substring(0, 40));
              return true;
            }

            // Case 2: OLD contains NEW (subset - nested element content)
            if (seenNormalized.length > normalizedText.length && seenNormalized.includes(normalizedText)) {
              console.log('[DetailedNotes] M68 Case 2 - Old contains new:', seenNormalized.substring(0, 40), '⊃', normalizedText.substring(0, 40));
              return true;
            }

            return false;
          });

          if (isOverlappingContent) {
            console.log('[DetailedNotes] M68 Skipping overlapping content:', text.substring(0, 80));
            return;
          }

          seenContent.add(contentHash);

          // Check 1: Is this an actual heading element?
          const isHeadingElement = ['h1', 'h2', 'h3', 'h4'].includes(tagName) ||
                          className.includes('doc-heading') ||
                          (tagName === 'div' && className.includes('heading'));

          // Check 2: Does this paragraph/div contain a bold section header?
          const boldHeader = !isHeadingElement ? extractBoldSectionHeader(element) : null;

          // Check 3: Is the entire element text a section header?
          const isTextHeader = !isHeadingElement && !boldHeader &&
                              looksLikeSectionHeader(text) &&
                              text.length < 80;

          // Determine if this is a section header
          const isNewSection = isHeadingElement || boldHeader || isTextHeader;
          const headerText = boldHeader || text;

          if (isNewSection && headerText.length > 2) {
            // Save previous section
            if (currentSection && currentContent.length > 0) {
              sections.push({
                ...currentSection,
                content: currentContent
              });
            }

            // Start new section
            currentSection = getSectionType(headerText);
            currentContent = [];

            // If there's content after the bold header in the same element, add it
            if (boldHeader) {
              const remainingHtml = removeHeaderFromContent(element, boldHeader);
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = remainingHtml;
              const remainingText = tempDiv.textContent?.trim() || '';

              if (remainingText.length > 5) {
                currentContent.push({
                  type: tagName,
                  className,
                  html: remainingHtml,
                  text: remainingText
                });
              }
            }
          } else if (currentSection) {
            // Add content to current section
            currentContent.push({
              type: tagName,
              className,
              html: element.innerHTML,
              text
            });
          } else {
            // PRINCIPLE: Content before first heading becomes overview
            // Only if it's substantial (not just metadata that slipped through)
            if (!currentSection && text.length > 50) {
              // Substantial text before any section header = real overview content
              currentSection = getSectionType('Overview');
              currentContent.push({
                type: tagName,
                className,
                html: element.innerHTML,
                text
              });
            }
            // Skip short pre-heading content (likely title/metadata noise)
          }
        });

        // Don't forget the last section
        if (currentSection && currentContent.length > 0) {
          sections.push({
            ...currentSection,
            content: currentContent
          });
        }
      };

      // Get all direct children and process
      const children = Array.from(body.children);
      if (children.length > 0) {
        processElements(children);
      }

      // PRINCIPLE: Only try nested processing if first pass yielded poor results
      // AND only process elements that weren't already captured
      if (sections.length <= 1 && children.length > 0 && !hitPagination) {
        console.log('[DetailedNotes] First pass yielded few sections, trying nested processing...');

        // Reset for fresh attempt
        sections.length = 0;
        currentSection = null;
        currentContent = [];
        seenContent.clear(); // Clear seen content for fresh pass
        hitPagination = false;

        // Flatten the structure - get paragraphs, headings, spans, and lists
        // PRINCIPLE: Prefer leaf-level elements (p, span, h*, li) over containers (div)
        // CRITICAL: Include ul/ol to preserve list structure for Core Concepts parsing
        const allElements = Array.from(body.querySelectorAll('h1, h2, h3, h4, p, span, ul, ol'));
        const seenTexts = new Set();

        // M68: PRINCIPLE-DRIVEN PARENT-CHILD DEDUPLICATION
        // When querySelectorAll selects BOTH parent (p, ul, ol) AND child (span) elements,
        // we get duplicate content because parent.textContent includes child.textContent.
        // Solution: Track parent elements and skip any element that's a descendant.
        // Priority: Keep parent elements (more complete content), skip child elements.
        const keptParentElements = new Set();

        const flatElements = allElements.filter(el => {
          const text = el.textContent?.trim();
          const tagName = el.tagName?.toLowerCase() || '';

          // Skip empty or too short
          if (!text || text.length < 3) return false;

          // M68: FIRST CHECK - Skip if this element is INSIDE an already-kept parent
          // This must be checked BEFORE we process the element itself
          for (const parentEl of keptParentElements) {
            if (parentEl.contains(el) && parentEl !== el) {
              // This element is a descendant of a kept parent - skip it
              console.log('[DetailedNotes] M68 Skipping descendant:', tagName, text.substring(0, 40));
              return false;
            }
          }

          // KEEP list elements (ul, ol) as they contain structured content (Core Concepts, etc.)
          // Lists need their full HTML structure preserved for parsing
          if (tagName === 'ul' || tagName === 'ol') {
            if (seenTexts.has(text)) return false;
            seenTexts.add(text);
            keptParentElements.add(el); // Track this parent element
            return true;
          }

          // M68: Track P elements that contain spans we're also selecting
          // This prevents <p><span>A</span><span>B</span></p> from adding both p and spans
          if (tagName === 'p' && el.querySelector('span')) {
            if (seenTexts.has(text)) return false;
            seenTexts.add(text);
            keptParentElements.add(el); // Track this parent element
            return true;
          }

          // Skip if element is a container (has block children)
          if (el.querySelector('p, div, h1, h2, h3, h4')) return false;
          // Skip elements with excessive text (likely containers that slipped through)
          if (text.length > 800 && el.children.length > 3) return false;
          // Skip duplicates (nested elements with same text)
          if (seenTexts.has(text)) return false;
          seenTexts.add(text);
          // Skip metadata UNLESS it looks like a section header
          if (isMetadataContent(text) && !looksLikeSectionHeader(text)) return false;
          if (isPaginationArtifact(text)) return false;
          return true;
        });

        if (flatElements.length > 0) {
          processElements(flatElements);
        }
      }

      // Validate we got meaningful sections
      if (sections.length === 0 || sections.every(s => s.content.length === 0)) {
        return null;
      }

      // Merge consecutive sections of the same type
      const mergedSections = [];
      for (const section of sections) {
        const last = mergedSections[mergedSections.length - 1];
        if (last && last.type === section.type) {
          last.content.push(...section.content);
        } else {
          mergedSections.push({ ...section });
        }
      }

      // M65: POST-PROCESSING - Deduplicate content within each section
      // This catches duplicates that slipped through parsing (e.g., concatenated blocks)
      mergedSections.forEach(section => {
        const seenTexts = new Set();
        const deduplicatedContent = [];

        section.content.forEach(item => {
          const normalizedText = item.text?.toLowerCase().replace(/\s+/g, ' ').trim() || '';

          // Skip if exact duplicate
          if (seenTexts.has(normalizedText)) {
            console.log('[DetailedNotes] Post-dedup: skipping duplicate in', section.type);
            return;
          }

          // Skip if this item is a SUPERSET containing other items
          // (indicates a concatenated block)
          const containsOthers = deduplicatedContent.some(existing => {
            const existingNormalized = existing.text?.toLowerCase().replace(/\s+/g, ' ').trim() || '';
            return existingNormalized.length > 30 && normalizedText.includes(existingNormalized);
          });

          if (containsOthers && normalizedText.length > 150) {
            console.log('[DetailedNotes] Post-dedup: skipping concatenated block in', section.type);
            return;
          }

          seenTexts.add(normalizedText);
          deduplicatedContent.push(item);
        });

        section.content = deduplicatedContent;
      });

      return mergedSections;
    } catch (err) {
      console.error('Error parsing notes content:', err);
      return null;
    }
  };

  // Memoized parsed sections
  const parsedSections = useMemo(() => {
    if (!content || useIframeFallback) return null;
    return parseNotesContent(content);
  }, [content, useIframeFallback]);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);
        setParseSuccess(false);
        setUseIframeFallback(false);

        if (!url) {
          setContent(null);
          setLoading(false);
          return;
        }

        console.log('DetailedNotes - Fetching content from:', url);

        // Try to fetch and parse content
        const docContent = await fetchDocumentContent(url);

        if (docContent) {
          setContent(docContent);

          // Check if content looks parseable (has substantial HTML structure)
          const hasStructure = docContent.includes('<p') ||
                             docContent.includes('<div') ||
                             docContent.includes('<h');

          if (hasStructure) {
            setParseSuccess(true);
          } else {
            // Content fetched but not structured - use iframe
            setUseIframeFallback(true);
          }
        } else {
          setUseIframeFallback(true);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading detailed notes:', err);
        // On error, try iframe fallback
        setUseIframeFallback(true);
        setLoading(false);
      }
    };

    loadContent();
  }, [url]);

  // Check if we should show iframe fallback after parsing attempt
  useEffect(() => {
    if (parseSuccess && !parsedSections && content) {
      console.log('Parsing failed, falling back to iframe');
      setUseIframeFallback(true);
    }
  }, [parseSuccess, parsedSections, content]);

  // Premium Loading State
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '300px',
        padding: '40px',
        background: `linear-gradient(135deg, ${lightBg} 0%, #ffffff 100%)`
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${lightBg}`,
          borderTop: `3px solid ${primaryColor}`,
          borderRadius: '50%',
          animation: 'dn-spin 0.8s cubic-bezier(0.5, 0, 0.5, 1) infinite'
        }}></div>
        <p style={{
          marginTop: '20px',
          color: '#666',
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontStyle: 'italic',
          fontSize: '1.1em'
        }}>
          Preparing scholarly notes...
        </p>
        <style dangerouslySetInnerHTML={{ __html: `
          @keyframes dn-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}} />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div style={{
        padding: '40px',
        margin: '20px',
        background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
        borderRadius: '16px',
        border: '1px solid #fecaca',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '2em', marginBottom: '12px' }}>⚠️</div>
        <p style={{ color: '#dc2626', fontWeight: 500 }}>{error}</p>
      </div>
    );
  }

  // Iframe Fallback - Google Docs viewer
  if (useIframeFallback && url) {
    const docId = extractGoogleDocId(url);
    if (docId) {
      return (
        <div className="detailed-notes-fallback" style={{
          padding: '0',
          background: `linear-gradient(180deg, ${lightBg} 0%, #ffffff 100%)`,
          minHeight: '100%'
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            padding: '48px 24px 32px',
            position: 'relative'
          }}>
            {/* Ornamental divider above title */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              gap: '16px'
            }}>
              <div style={{ width: '60px', height: '1px', background: `linear-gradient(90deg, transparent, ${accentGold})` }}></div>
              <div style={{ width: '8px', height: '8px', background: accentGold, transform: 'rotate(45deg)' }}></div>
              <div style={{ width: '60px', height: '1px', background: `linear-gradient(90deg, ${accentGold}, transparent)` }}></div>
            </div>

            <h2 style={{
              color: primaryColor,
              fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
              fontWeight: 600,
              fontFamily: 'Georgia, "Times New Roman", serif',
              letterSpacing: '-0.02em',
              margin: '0 0 12px 0'
            }}>
              Detailed Notes
            </h2>

            <p style={{
              color: '#6b7280',
              fontSize: '1.05em',
              fontStyle: 'italic',
              margin: 0,
              fontFamily: 'Georgia, "Times New Roman", serif'
            }}>
              Comprehensive lecture documentation
            </p>

            {/* Ornamental divider below subtitle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '24px',
              gap: '8px'
            }}>
              <div style={{ width: '40px', height: '2px', background: primaryColor, borderRadius: '2px' }}></div>
              <div style={{ width: '6px', height: '6px', background: accentGold, borderRadius: '50%' }}></div>
              <div style={{ width: '40px', height: '2px', background: primaryColor, borderRadius: '2px' }}></div>
            </div>
          </div>

          {/* Iframe Container */}
          <div style={{
            padding: '0 24px 48px',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <div style={{
              background: cardBg,
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              <iframe
                src={`https://docs.google.com/document/d/${docId}/preview`}
                width="100%"
                height="700px"
                title="Detailed Notes Document"
                style={{ display: 'block', border: 'none' }}
              />
            </div>
            <p style={{
              textAlign: 'center',
              marginTop: '16px',
              color: '#6b7280',
              fontSize: '0.9em',
              fontFamily: 'Georgia, "Times New Roman", serif'
            }}>
              <a
                href={`https://docs.google.com/document/d/${docId}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: primaryColor, textDecoration: 'none' }}
              >
                Open in Google Docs ↗
              </a>
            </p>
          </div>
        </div>
      );
    }
  }

  // Parsed Content Render - Premium "Scholarly Manuscript" design
  if (parsedSections && parsedSections.length > 0) {
    return (
      <div className="detailed-notes-container" style={{
        padding: '0',
        background: `linear-gradient(180deg, ${lightBg} 0%, #ffffff 100%)`,
        minHeight: '100%'
      }}>
        {/* Elegant Header Section */}
        <div style={{
          textAlign: 'center',
          padding: '48px 24px 32px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative geometric pattern background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${primaryColor.replace('#', '')}' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none'
          }}></div>

          {/* Ornamental divider above title */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            gap: '16px'
          }}>
            <div style={{ width: '60px', height: '1px', background: `linear-gradient(90deg, transparent, ${accentGold})` }}></div>
            <div style={{ width: '8px', height: '8px', background: accentGold, transform: 'rotate(45deg)' }}></div>
            <div style={{ width: '60px', height: '1px', background: `linear-gradient(90deg, ${accentGold}, transparent)` }}></div>
          </div>

          {/* Main Title */}
          <h2 style={{
            color: primaryColor,
            fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
            fontWeight: 600,
            fontFamily: 'Georgia, "Times New Roman", serif',
            letterSpacing: '-0.02em',
            margin: '0 0 12px 0',
            position: 'relative'
          }}>
            Detailed Notes
          </h2>

          {/* Subtitle */}
          <p style={{
            color: '#6b7280',
            fontSize: '1.05em',
            fontStyle: 'italic',
            margin: 0,
            fontFamily: 'Georgia, "Times New Roman", serif'
          }}>
            Comprehensive insights from this lecture
          </p>

          {/* Ornamental divider below subtitle */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '24px',
            gap: '8px'
          }}>
            <div style={{ width: '40px', height: '2px', background: primaryColor, borderRadius: '2px' }}></div>
            <div style={{ width: '6px', height: '6px', background: accentGold, borderRadius: '50%' }}></div>
            <div style={{ width: '40px', height: '2px', background: primaryColor, borderRadius: '2px' }}></div>
          </div>

        </div>

        {/* Sections Content */}
        <div style={{
          padding: '0 24px 48px',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {parsedSections.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className="dn-section"
              style={{
                marginBottom: '32px',
                animation: `dn-fadeInUp 0.6s ease-out ${0.1 + sectionIndex * 0.1}s both`
              }}
            >
              {/* Section Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: `2px solid ${section.color}20`
              }}>
                <span style={{
                  fontSize: '1.5em',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `${section.color}10`,
                  borderRadius: '10px'
                }}>
                  {section.icon}
                </span>
                <h3 style={{
                  margin: 0,
                  color: section.color,
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '1.4em',
                  fontWeight: 600,
                  letterSpacing: '-0.01em'
                }}>
                  {section.label}
                </h3>
              </div>

              {/* Section Content - Smart rendering based on section type */}
              <div className="dn-section-content">
                {(() => {
                  // Combine all content HTML for this section and clean metadata
                  const rawHtml = section.content.map(item => item.html).join(' ');
                  const combinedHtml = cleanMetadataFromHtml(rawHtml);

                  // QUOTES SECTION - Elegant blockquotes with parsed quote/attribution
                  // M71: Parse "quote text" - Attribution format for better rendering
                  if (section.type === 'quotes' || section.type === 'notable' || section.type === 'notable quotes') {
                    // Parse quotes to separate quote text from attribution
                    const parseQuote = (text) => {
                      if (!text) return { quote: '', attribution: null };

                      // M71 FIX: Helper to strip ALL quote variants from start and end
                      const stripQuoteMarks = (str) => str
                        .replace(/^[\u0022\u201C\u201D""]+/g, '') // Leading quotes
                        .replace(/[\u0022\u201C\u201D""]+$/g, '') // Trailing quotes
                        .trim();

                      // M71 ENHANCED: Handle multiple quote formats
                      // Format 1: "quote text" - Attribution (with dash separator)
                      // Format 2: "quote text" Attribution (closing quote, no dash)
                      // Format 3: "quote text.Attribution" (no closing quote between)
                      // NOTE: Use + to match ONE OR MORE opening quotes (data often has "" double)

                      // Try Format 1 first: with dash separator
                      const dashPattern = /^[""\u201C]+(.+?)[""\u201D]\s*[-–—]\s*(.+)$/s;
                      const dashMatch = text.match(dashPattern);
                      if (dashMatch) {
                        return {
                          quote: stripQuoteMarks(dashMatch[1]),
                          attribution: stripQuoteMarks(dashMatch[2])
                        };
                      }

                      // Try Format 2: closing quote followed by attribution (no dash)
                      const noDashPattern = /^[""\u201C]+(.+?)[""\u201D]\s+(.+)$/s;
                      const noDashMatch = text.match(noDashPattern);
                      if (noDashMatch && noDashMatch[2].length > 3) {
                        return {
                          quote: stripQuoteMarks(noDashMatch[1]),
                          attribution: stripQuoteMarks(noDashMatch[2])
                        };
                      }

                      // Format 3: Try to find a period followed by capital letter as split point
                      // e.g., "quote text.Attribution Name" or ""quote text.Attribution Name"
                      // Note: Raw data often has DOUBLE quotes ""text" - use * for zero or more
                      const periodSplitPattern = /^[""\u201C\u201D\u0022]*(.+?\.)\s*([A-Z][^""\u201D\u0022]+)[""\u201C\u201D\u0022]*$/s;
                      const periodMatch = text.match(periodSplitPattern);
                      // PRINCIPLE: Don't split on ellipsis ("...") — it's a continuation, not an attribution separator
                      if (periodMatch && periodMatch[2].length > 3 && periodMatch[2].length < 80 && !periodMatch[1].endsWith('...')) {
                        // Helper to strip ALL quote mark variants
                        const stripQuotes = (str) => str
                          .replace(/^[\u0022\u201C\u201D""]+/g, '') // Leading quotes
                          .replace(/[\u0022\u201C\u201D""]+$/g, '') // Trailing quotes
                          .trim();
                        return {
                          quote: stripQuotes(periodMatch[1]),
                          attribution: stripQuotes(periodMatch[2])
                        };
                      }

                      // Fallback: Return whole text as quote, strip ALL quote mark variants
                      const stripAllQuotes = (str) => str
                        .replace(/^[\u0022\u201C\u201D""]+/g, '')
                        .replace(/[\u0022\u201C\u201D""]+$/g, '')
                        .trim();
                      return { quote: stripAllQuotes(text), attribution: null };
                    };

                    return section.content.map((item, itemIndex) => {
                      const parsed = parseQuote(item.text || '');

                      return (
                        <div
                          key={itemIndex}
                          className="dn-quote-card"
                          style={{
                            margin: '16px 0',
                            background: `linear-gradient(135deg, ${cardBg} 0%, #fffdf8 100%)`,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                            border: `1px solid ${accentGold}15`,
                            animation: `dn-fadeInUp 0.5s ease-out ${0.1 * itemIndex}s both`
                          }}
                        >
                          {/* Quote content area */}
                          <div style={{
                            padding: '28px 32px 20px 48px',
                            position: 'relative'
                          }}>
                            {/* Large decorative quote mark */}
                            <span style={{
                              position: 'absolute',
                              top: '16px',
                              left: '16px',
                              fontSize: '3em',
                              color: accentGold,
                              opacity: 0.3,
                              fontFamily: 'Georgia, serif',
                              lineHeight: 1
                            }}>"</span>

                            {/* Quote text */}
                            <p style={{
                              margin: 0,
                              fontFamily: '"Cormorant Garamond", Georgia, serif',
                              fontSize: '1.2em',
                              fontStyle: 'italic',
                              color: '#374151',
                              lineHeight: 1.75,
                              fontWeight: 500
                            }}>
                              {parsed.quote}
                            </p>
                          </div>

                          {/* Attribution footer */}
                          {parsed.attribution && (
                            <div style={{
                              padding: '14px 32px',
                              background: `linear-gradient(90deg, ${accentGold}12 0%, transparent 100%)`,
                              borderTop: `1px solid ${accentGold}20`,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <span style={{
                                width: '24px',
                                height: '2px',
                                background: accentGold,
                                borderRadius: '2px'
                              }}></span>
                              <span style={{
                                fontFamily: '"Inter", -apple-system, sans-serif',
                                fontSize: '0.9em',
                                color: '#6b7280',
                                fontWeight: 500
                              }}>
                                {parsed.attribution}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    });
                  }

                  // CORE CONCEPTS & PRACTICAL APPLICATIONS - Term-definition cards
                  // PRINCIPLE: Parse "**Term:** Definition" patterns into elegant concept cards
                  // Universal approach: Handle Google Docs list items with bold terms
                  // Both sections share the same structure: bold term followed by ": definition"
                  const isTermDefinitionSection = section.type === 'core concepts' ||
                    section.type === 'practical applications' || section.type === 'practical' || section.type === 'applications';
                  if (isTermDefinitionSection) {
                    // Debug: Log the raw content structure
                    console.log('[DetailedNotes] Core Concepts input:', {
                      itemCount: section.content.length,
                      htmlSample: combinedHtml.substring(0, 500),
                      hasListItems: combinedHtml.includes('<li'),
                      hasBoldSpans: combinedHtml.includes('font-weight')
                    });

                    const parseConcepts = (html, items) => {
                      const concepts = [];
                      const seenTerms = new Set(); // Deduplication

                      // STRATEGY 1: Parse <li> elements from HTML (most reliable for Google Docs lists)
                      // Google Docs exports: <li><span style="font-weight:700">Term:</span> Definition</li>
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = html;

                      // Find all list items
                      const listItems = tempDiv.querySelectorAll('li');
                      if (listItems.length > 0) {
                        listItems.forEach(li => {
                          // Look for bold element (span with font-weight, strong, or b)
                          const boldEl = li.querySelector('span[style*="font-weight"], strong, b');
                          if (boldEl) {
                            let term = boldEl.textContent?.trim() || '';
                            // Remove trailing colon/dash from term
                            term = term.replace(/[:–—]\s*$/, '').trim();

                            // Get definition: everything after the bold element
                            // Clone the li, remove the bold element, get remaining text
                            const liClone = li.cloneNode(true);
                            const boldInClone = liClone.querySelector('span[style*="font-weight"], strong, b');
                            if (boldInClone) boldInClone.remove();
                            let definition = liClone.textContent?.trim() || '';
                            // Clean up leading colons/dashes from definition
                            definition = definition.replace(/^[:–—]\s*/, '').trim();

                            if (term && definition && term.length < 60 && definition.length > 5 && !seenTerms.has(term.toLowerCase())) {
                              seenTerms.add(term.toLowerCase());
                              concepts.push({ term, definition });
                            }
                          } else {
                            // No explicit bold - try pattern matching on text
                            const liText = li.textContent?.trim() || '';
                            // Pattern: Term: Definition (term is 1-4 words before colon)
                            const textMatch = liText.match(/^([A-Za-z\u0027\u2019\u0600-\u06FF\-'']+(?:\s+[A-Za-z\u0027\u2019\u0600-\u06FF\-'']+){0,3})\s*[:–—]\s*(.+)$/);
                            if (textMatch) {
                              const term = textMatch[1]?.trim();
                              const definition = textMatch[2]?.trim();
                              if (term && definition && term.length < 60 && definition.length > 5 && !seenTerms.has(term.toLowerCase())) {
                                seenTerms.add(term.toLowerCase());
                                concepts.push({ term, definition });
                              }
                            }
                          }
                        });
                      }

                      // STRATEGY 2: Parse from individual content items if list parsing didn't work
                      if (concepts.length === 0 && items && items.length > 0) {
                        items.forEach(item => {
                          const itemHtml = item.html || '';
                          const itemDiv = document.createElement('div');
                          itemDiv.innerHTML = itemHtml;

                          // Check for bold elements in this item
                          const boldEl = itemDiv.querySelector('span[style*="font-weight"], strong, b');
                          if (boldEl) {
                            let term = boldEl.textContent?.trim().replace(/[:–—]\s*$/, '') || '';
                            boldEl.remove();
                            let definition = itemDiv.textContent?.trim().replace(/^[:–—]\s*/, '') || '';

                            if (term && definition && term.length < 60 && definition.length > 5 && !seenTerms.has(term.toLowerCase())) {
                              seenTerms.add(term.toLowerCase());
                              concepts.push({ term, definition });
                            }
                          } else {
                            // Text-based pattern matching
                            const itemText = item.text || '';
                            const itemMatch = itemText.match(/^[•\-\*]?\s*([A-Za-z\u0027\u2019\u0600-\u06FF\-'']+(?:\s+[A-Za-z\u0027\u2019\u0600-\u06FF\-'']+){0,3})\s*[:–—]\s*(.+)$/);
                            if (itemMatch) {
                              const term = itemMatch[1]?.trim();
                              const definition = itemMatch[2]?.trim();
                              if (term && definition && term.length < 60 && definition.length > 5 && !seenTerms.has(term.toLowerCase())) {
                                seenTerms.add(term.toLowerCase());
                                concepts.push({ term, definition });
                              }
                            }
                          }
                        });
                      }

                      // STRATEGY 3: Parse from combined text using line breaks/bullets as separators
                      if (concepts.length === 0) {
                        const text = tempDiv.textContent || '';
                        // Split by common separators (bullet, newline, or multiple spaces indicating new item)
                        const segments = text.split(/[•\-\*]|\n|(?:  +)/g).filter(s => s.trim());

                        segments.forEach(segment => {
                          const segMatch = segment.match(/^([A-Za-z\u0027\u2019\u0600-\u06FF\-'']+(?:\s+[A-Za-z\u0027\u2019\u0600-\u06FF\-'']+){0,3})\s*[:–—]\s*(.+)$/);
                          if (segMatch) {
                            const term = segMatch[1]?.trim();
                            const definition = segMatch[2]?.trim();
                            if (term && definition && term.length < 60 && definition.length > 5 && !seenTerms.has(term.toLowerCase())) {
                              seenTerms.add(term.toLowerCase());
                              concepts.push({ term, definition });
                            }
                          }
                        });
                      }

                      console.log('[DetailedNotes] Core Concepts parsed:', concepts.length, 'concepts from', listItems.length, 'list items');
                      return concepts;
                    };

                    const concepts = parseConcepts(combinedHtml, section.content);

                    if (concepts.length > 0) {
                      return (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '12px',
                          marginTop: '8px'
                        }}>
                          {concepts.map((concept, idx) => (
                            <div
                              key={idx}
                              className="dn-concept-card"
                              style={{
                                flex: '1 1 calc(50% - 6px)',
                                minWidth: '280px',
                                background: `linear-gradient(135deg, ${cardBg} 0%, #f8f9ff 100%)`,
                                borderRadius: '12px',
                                padding: '16px 20px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                border: `1px solid ${section.color}15`,
                                animation: `dn-fadeInUp 0.4s ease-out ${0.08 * idx}s both`
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '8px'
                              }}>
                                <span style={{
                                  padding: '4px 10px',
                                  background: `${section.color}15`,
                                  color: section.color,
                                  borderRadius: '6px',
                                  fontSize: '0.9em',
                                  fontWeight: 600,
                                  fontFamily: '"Cormorant Garamond", Georgia, serif'
                                }}>
                                  {concept.term}
                                </span>
                              </div>
                              <p style={{
                                margin: 0,
                                fontSize: '0.9em',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                fontFamily: '"Inter", -apple-system, sans-serif'
                              }}>
                                {concept.definition}
                              </p>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    // Fall through to default rendering if parsing fails
                  }

                  // QUESTIONS SECTIONS - Numbered question cards
                  // M60 types: 'key questions addressed', 'reflection questions' + legacy support
                  // M70: Robust type checking - also match 'questions' anywhere in section.type
                  const isQuestionsSection = ['key questions', 'key questions addressed', 'reflection', 'reflection questions'].includes(section.type) ||
                    section.type?.toLowerCase().includes('question');
                  if (isQuestionsSection) {
                    // M70 ENHANCED: Parse questions from combined HTML content
                    // Google Docs exports all list items together - we need to extract each LI as separate question

                    const extractQuestions = (contentItems) => {
                      // Combine all HTML from content items
                      const allHtml = contentItems.map(item => item.html || '').join(' ');

                      // Parse HTML to extract individual questions
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = allHtml;

                      const questions = [];

                      // Method 1: Find all LI elements (most common from Google Docs)
                      const listItems = tempDiv.querySelectorAll('li');
                      if (listItems.length > 0) {
                        listItems.forEach(li => {
                          const text = li.textContent?.trim();
                          if (text && text.length > 5 && !/^\d+\.?$/.test(text)) {
                            // Clean any leading numbers from the text
                            const cleanedText = text.replace(/^\s*\d+\.?\s*/, '');
                            if (cleanedText) {
                              questions.push(cleanedText);
                            }
                          }
                        });
                      }

                      // Method 2: If no LI elements, try splitting by question marks or line breaks
                      if (questions.length === 0) {
                        const fullText = tempDiv.textContent || '';
                        // Split by question mark followed by space or end
                        const splitQuestions = fullText.split(/\?\s*/).filter(q => q.trim().length > 10);
                        splitQuestions.forEach(q => {
                          const cleanedText = q.replace(/^\s*\d+\.?\s*/, '').trim();
                          if (cleanedText) {
                            questions.push(cleanedText + '?');
                          }
                        });
                      }

                      // Method 3: Fallback - use individual content items that aren't just numbers
                      if (questions.length === 0) {
                        contentItems.forEach(item => {
                          const text = item.text?.trim();
                          if (text && text.length > 5 && !/^\d+\.?$/.test(text)) {
                            const cleanedText = text.replace(/^\s*\d+\.?\s*/, '');
                            if (cleanedText) {
                              questions.push(cleanedText);
                            }
                          }
                        });
                      }

                      return questions;
                    };

                    const questions = extractQuestions(section.content);

                    return questions.map((questionText, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="dn-question-card"
                        style={{
                          margin: '12px 0',
                          padding: '18px 20px 18px 68px',
                          background: cardBg,
                          borderRadius: '14px',
                          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.05)',
                          border: '1px solid rgba(0, 0, 0, 0.05)',
                          position: 'relative',
                          lineHeight: 1.7,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                          color: '#374151',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          animation: `dn-fadeInUp 0.5s ease-out ${0.08 * itemIndex}s both`
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          left: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '40px',
                          height: '40px',
                          background: `linear-gradient(135deg, ${section.color}15 0%, ${section.color}08 100%)`,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: '"Cormorant Garamond", Georgia, serif',
                          fontSize: '1.4em',
                          fontWeight: 700,
                          color: section.color,
                          border: `1px solid ${section.color}20`
                        }}>{itemIndex + 1}</span>
                        <p style={{ margin: 0 }}>{questionText}</p>
                      </div>
                    ));
                  }

                  // REFERENCES SECTION - Elegant scripture cards with Arabic text
                  // PRINCIPLE: Universal parsing that handles multiple content formats
                  // M60 types: 'quranic references', 'hadith references' + legacy 'references', 'quranic', 'hadith'
                  const isReferencesSection = ['references', 'quranic', 'hadith', 'quranic references', 'hadith references'].includes(section.type);

                  if (isReferencesSection) {
                    const parseReferences = (html, sectionType) => {
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = html;
                      const text = tempDiv.textContent || '';

                      const refs = [];
                      const seenTranslations = new Set(); // Deduplication

                      // M60: Determine forced reference type based on section
                      // If this is a dedicated Quran or Hadith section, override detected types
                      const forceQuran = sectionType === 'quranic references' || sectionType === 'quranic';
                      const forceHadith = sectionType === 'hadith references' || sectionType === 'hadith';

                      // STEP 1: Find ALL URLs (quran.com AND al-islam.org) - URLs are reliable anchors
                      // M73 FIX: Use precise patterns to avoid capturing trailing text (e.g., "quran.com/39/9Prophetic")
                      // quran.com format: /chapter/verse or /chapter:verse-verse (digits only after domain)
                      // al-islam.org: more flexible path structure
                      const allUrlPattern = /(https?:\/\/(?:quran\.com\/\d+(?:[:/]\d+)?(?:-\d+)?|www\.al-islam\.org[^\s]+))/g;
                      const urls = [];
                      let urlMatch;

                      while ((urlMatch = allUrlPattern.exec(text)) !== null) {
                        const url = urlMatch[1];
                        const quranMatch = url.match(/quran\.com\/(\d+)(?:\/(\d+))?/);
                        urls.push({
                          url,
                          surah: quranMatch?.[1],
                          ayah: quranMatch?.[2],
                          endIndex: urlMatch.index + url.length,
                          startIndex: urlMatch.index,
                          isQuran: url.includes('quran.com'),
                          isHadith: url.includes('al-islam.org')
                        });
                      }

                      // M68: MULTI-REFERENCE EXTRACTION - Find ALL hadith/quran structures in each segment
                      // A segment may contain multiple references before a single URL
                      // Pattern: [Reference prefix] Arabic text "English translation" (Source)

                      // Helper: Extract all complete hadith structures from text
                      const extractAllHadiths = (segment, urlInfo) => {
                        const results = [];

                        // M68 PATTERN: Match complete hadith structure:
                        // Optional reference prefix + Arabic text + "quoted translation" + optional (source)
                        // This regex finds: [optional prefix] [Arabic] "translation" [(source)]
                        const hadithPattern = /(?:(?:Hadith\s+of\s+the\s+Prophet|Imam\s+\w+)[^""]*?)?([\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\s]+)\s*[""\u201C]([^""\u201D]+)[""\u201D](?:\s*\(([^)]+)\))?/g;

                        let match;
                        while ((match = hadithPattern.exec(segment)) !== null) {
                          const fullMatch = match[0];
                          const arabic = match[1]?.trim();
                          const translation = match[2]?.trim();
                          const source = match[3]?.trim();

                          if (arabic && translation) {
                            // Extract reference prefix if present (stop at Arabic characters)
                            let reference = source || 'Hadith';
                            // Match prefix but STOP before Arabic text (Unicode range \u0600-\u06FF)
                            const prefixMatch = fullMatch.match(/^((?:Hadith\s+of\s+the\s+Prophet|Imam\s+\w+)(?:\s*\([^)]+\))?(?:\s*-\s*[^،\u0600-\u06FF]+)?)/i);
                            if (prefixMatch) {
                              reference = prefixMatch[1].trim().replace(/\s+$/, '');
                            }

                            results.push({
                              arabic,
                              translation,
                              reference,
                              url: urlInfo?.url || null,
                              isQuran: urlInfo?.isQuran || false,
                              isHadith: true
                            });
                          }
                        }

                        return results;
                      };

                      // STEP 2: For each URL, extract content from segment before it
                      urls.forEach((urlInfo, i) => {
                        const segmentStart = i === 0 ? 0 : urls[i - 1].endIndex;
                        let segment = text.substring(segmentStart, urlInfo.startIndex);

                        // Handle "Source:" prefix before URL
                        segment = segment.replace(/\s*Source:\s*$/i, '').trim();

                        // M68: For hadith URLs, try multi-reference extraction first
                        if (urlInfo.isHadith) {
                          const extractedHadiths = extractAllHadiths(segment, urlInfo);
                          if (extractedHadiths.length > 0) {
                            // Add all extracted hadiths (with deduplication)
                            extractedHadiths.forEach(h => {
                              if (!seenTranslations.has(h.translation)) {
                                seenTranslations.add(h.translation);
                                refs.push({
                                  type: forceQuran ? 'quran' : 'hadith',
                                  source: 'Hadith',
                                  reference: h.reference,
                                  arabic: h.arabic,
                                  translation: h.translation,
                                  url: h.url
                                });
                              }
                            });
                            return; // Skip single-reference extraction for this segment
                          }
                        }

                        // FALLBACK: Single reference extraction (original logic for Quran and simple hadiths)
                        let arabic = '';
                        let translation = '';
                        let reference = '';

                        // UNIVERSAL TRANSLATION EXTRACTION
                        // Priority 1: Look for translation in QUOTES "..." (12/04/25 format)
                        const quotedMatch = segment.match(/(.+?)[""\u201C]([^""\u201D]+)[""\u201D]\s*$/);

                        if (quotedMatch) {
                          arabic = quotedMatch[1]?.trim();
                          translation = quotedMatch[2]?.trim();
                        } else {
                          // Priority 2: Look for translation in PARENTHESES (...) (12/13/25 format)
                          // Skip verse numbers like (1), (2), (3) - find the LAST non-numeric parenthetical
                          const parentheticals = [...segment.matchAll(/\(([^)]+)\)/g)];
                          const translationParen = parentheticals.filter(m => !m[1].match(/^\d+$/)).pop();

                          if (translationParen) {
                            arabic = segment.substring(0, translationParen.index).trim();
                            translation = translationParen[1].trim();
                            // Strip quotes from inside parenthetical
                            translation = translation.replace(/^[""\u201C\u201D']+/, '').replace(/[""\u201C\u201D']+$/, '').trim();
                          }
                        }

                        // Extract reference (Quran X:Y or similar)
                        if (urlInfo.isQuran && urlInfo.surah) {
                          const refMatch = arabic.match(/^Quran\s*(\d+(?::\d+(?:-\d+)?)?)/i);
                          if (refMatch) {
                            reference = refMatch[1];
                            arabic = arabic.replace(/^Quran\s*\d+(?::\d+(?:-\d+)?)?\s*:?\s*/i, '').trim();
                          } else {
                            reference = urlInfo.ayah ? `${urlInfo.surah}:${urlInfo.ayah}` : urlInfo.surah;
                          }
                        } else if (urlInfo.isHadith) {
                          // Extract hadith reference like "Imam Ali (AS) - Nahjul Balagha, Wisdom 147"
                          const hadithRefMatch = arabic.match(/^((?:Imam\s*\w+[^:]*|Hadith[^:]*|Prophet[^:]*))/i);
                          if (hadithRefMatch) {
                            reference = hadithRefMatch[1].trim();
                            arabic = arabic.replace(new RegExp('^' + hadithRefMatch[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*:?\\s*', 'i'), '').trim();
                          } else {
                            reference = 'Hadith';
                          }
                        }

                        // Clean Arabic text - remove verse numbers (1), (2) from display
                        arabic = arabic.replace(/\s*\(\d+\)/g, ' ').replace(/\s+/g, ' ').trim();

                        // M73 FIX: Validate that "arabic" actually contains Arabic Unicode characters
                        // Prevents English text like "Say," from being incorrectly treated as Arabic
                        const hasActualArabic = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/.test(arabic);
                        if (!hasActualArabic) {
                          // The "arabic" field contains English text (e.g., "Say,")
                          // This often happens when the translation has a leading word before quotes
                          // Prepend it to the translation if it looks like English text
                          const englishPrefix = arabic.replace(/^(Quran\s*\d+[:\d\-]*\s*:?\s*)/i, '').trim();
                          if (englishPrefix && translation) {
                            translation = englishPrefix + ' "' + translation + '"';
                          }
                          arabic = '';
                        }

                        // Deduplicate and add valid references (translation is required, arabic is optional)
                        if (translation && !seenTranslations.has(translation)) {
                          seenTranslations.add(translation);
                          // M60: Force type based on section, otherwise use URL detection
                          const detectedType = forceQuran ? 'quran' : forceHadith ? 'hadith' : (urlInfo.isQuran ? 'quran' : 'hadith');
                          const detectedSource = detectedType === 'quran' ? 'Quran' : 'Hadith';
                          refs.push({
                            type: detectedType,
                            source: detectedSource,
                            reference,
                            arabic,
                            translation,
                            url: urlInfo.url
                          });
                        }
                      });

                      // STEP 3: Handle standalone references without URLs
                      // Pattern: Arabic "translation" (source) - no URL
                      // M72 FIX: Detect type by source content, not hardcoded
                      if (!forceQuran) {
                        const standalonePattern = /([\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF\s]+)\s*[""\u201C]([^""\u201D]+)[""\u201D]\s*\(([^)]+)\)/g;
                        let standaloneMatch;
                        while ((standaloneMatch = standalonePattern.exec(text)) !== null) {
                          const arabic = standaloneMatch[1].trim();
                          const translation = standaloneMatch[2].trim();
                          const source = standaloneMatch[3].trim();

                          // Only add if not a duplicate and not a verse number source
                          if (!seenTranslations.has(translation) && !source.match(/^\d+$/)) {
                            seenTranslations.add(translation);

                            // M72: Detect type by source content - if source contains "quran", it's Quran
                            const isQuranSource = /quran/i.test(source);
                            const detectedType = forceHadith ? 'hadith' : (isQuranSource ? 'quran' : 'hadith');
                            const detectedSourceLabel = isQuranSource ? 'Quran' : 'Hadith';

                            // M72: Try to extract Quran reference (e.g., "108:1-3") from text before Arabic
                            let reference = source.replace(/^Source:\s*/i, '').trim();
                            if (isQuranSource) {
                              // Look backwards in text for "Quran X:Y" pattern before this Arabic
                              const textBeforeArabic = text.substring(0, standaloneMatch.index);
                              const quranRefMatch = textBeforeArabic.match(/Quran\s*(\d+(?::\d+(?:-\d+)?)?)\s*[:\s]*$/i);
                              if (quranRefMatch) {
                                reference = quranRefMatch[1]; // e.g., "108:1-3"
                              }
                            }

                            refs.push({
                              type: detectedType,
                              source: detectedSourceLabel,
                              reference,
                              arabic,
                              translation,
                              url: null
                            });
                          }
                        }
                      }

                      // STEP 4: URL-less fallback — Parse text-only references from HTML structure
                      // PRINCIPLE: Handle "Quran X:Y: Translation text" and "Hadith: Translation text"
                      // patterns from Google Docs exports that don't include quran.com/al-islam.org URLs
                      // Uses FULL li text for reference extraction (handles both pre-fix and post-fix HTML
                      // where colon may or may not split "Quran 5:35" across bold/non-bold boundaries)
                      if (refs.length === 0) {
                        const listItems = tempDiv.querySelectorAll('li');
                        if (listItems.length > 0) {
                          listItems.forEach(li => {
                            const liText = li.textContent?.trim() || '';

                            // PRIMARY: Full-text pattern match on "Quran X:Y: translation"
                            // Works regardless of bold/non-bold HTML structure
                            const textRefMatch = liText.match(/^Quran\s+(\d+:\d+(?:-\d+)?)\s*:\s*(.+)$/i);
                            if (textRefMatch && !seenTranslations.has(textRefMatch[2])) {
                              seenTranslations.add(textRefMatch[2]);
                              refs.push({
                                type: forceHadith ? 'hadith' : 'quran',
                                source: 'Quran',
                                reference: textRefMatch[1],
                                arabic: '',
                                translation: textRefMatch[2].trim(),
                                url: null
                              });
                              return; // next li
                            }

                            // SECONDARY: Hadith pattern from bold text
                            const boldEl = li.querySelector('strong, b');
                            if (boldEl) {
                              const refText = boldEl.textContent?.trim() || '';
                              const hadithMatch = refText.match(/^(Hadith|Imam|Prophet|Nahjul)/i);
                              if (hadithMatch) {
                                const liClone = li.cloneNode(true);
                                const boldInClone = liClone.querySelector('strong, b');
                                if (boldInClone) boldInClone.remove();
                                let translation = liClone.textContent?.trim().replace(/^[:–—]\s*/, '') || '';
                                if (translation && !seenTranslations.has(translation)) {
                                  seenTranslations.add(translation);
                                  refs.push({
                                    type: forceQuran ? 'quran' : 'hadith',
                                    source: 'Hadith',
                                    reference: refText.replace(/[:–—]\s*$/, '').trim(),
                                    arabic: '',
                                    translation,
                                    url: null
                                  });
                                }
                              }
                            }
                          });
                        }

                        // Fallback for non-list content: split by "Quran X:Y:" pattern
                        if (refs.length === 0) {
                          const fullText = tempDiv.textContent || '';
                          const refPattern = /Quran\s+(\d+:\d+(?:-\d+)?)\s*:\s*([^Q]+?)(?=Quran\s+\d+:\d+|$)/gi;
                          let refMatch;
                          while ((refMatch = refPattern.exec(fullText)) !== null) {
                            const translation = refMatch[2].trim();
                            if (translation && !seenTranslations.has(translation)) {
                              seenTranslations.add(translation);
                              refs.push({
                                type: forceHadith ? 'hadith' : 'quran',
                                source: 'Quran',
                                reference: refMatch[1],
                                arabic: '',
                                translation,
                                url: null
                              });
                            }
                          }
                        }
                      }

                      return refs;
                    };

                    const references = parseReferences(combinedHtml, section.type);

                    // If we successfully parsed references, render them as cards
                    if (references.length > 0) {
                      return references.map((ref, refIndex) => (
                        <div
                          key={refIndex}
                          className="dn-reference-card"
                          style={{
                            margin: '16px 0',
                            background: `linear-gradient(135deg, ${cardBg} 0%, ${ref.type === 'quran' ? '#fffef5' : '#f8fffe'} 100%)`,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                            border: `1px solid ${ref.type === 'quran' ? accentGold : '#059669'}15`,
                            animation: `dn-fadeInUp 0.5s ease-out ${0.1 * refIndex}s both`
                          }}
                        >
                          {/* Reference header with source badge */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '14px 20px',
                            background: ref.type === 'quran'
                              ? `linear-gradient(90deg, ${accentGold}15 0%, transparent 100%)`
                              : 'linear-gradient(90deg, #05966915 0%, transparent 100%)',
                            borderBottom: '1px solid rgba(0,0,0,0.05)'
                          }}>
                            <span style={{
                              padding: '4px 12px',
                              background: ref.type === 'quran' ? accentGold : '#059669',
                              color: '#fff',
                              borderRadius: '6px',
                              fontSize: '0.75em',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em'
                            }}>
                              {ref.source}
                            </span>
                            <span style={{
                              fontFamily: '"Cormorant Garamond", Georgia, serif',
                              fontSize: '1.1em',
                              fontWeight: 600,
                              color: ref.type === 'quran' ? accentGold : '#059669'
                            }}>
                              {ref.reference}
                            </span>
                            {ref.url && (
                              <a
                                href={ref.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  marginLeft: 'auto',
                                  fontSize: '0.85em',
                                  color: primaryColor,
                                  textDecoration: 'none',
                                  opacity: 0.7
                                }}
                              >
                                View Source ↗
                              </a>
                            )}
                          </div>

                          {/* Arabic text */}
                          {ref.arabic && (
                            <div style={{
                              padding: '20px 24px',
                              textAlign: 'right',
                              direction: 'rtl',
                              fontFamily: '"Amiri", "Traditional Arabic", "Scheherazade New", serif',
                              fontSize: '1.4em',
                              lineHeight: 2,
                              color: '#1f2937',
                              background: 'rgba(0,0,0,0.01)'
                            }}>
                              {ref.arabic}
                            </div>
                          )}

                          {/* English translation */}
                          {ref.translation && (
                            <div style={{
                              padding: '16px 24px 20px',
                              fontFamily: '"Inter", -apple-system, sans-serif',
                              fontSize: '0.95em',
                              lineHeight: 1.7,
                              color: '#4b5563',
                              fontStyle: 'italic',
                              borderTop: '1px dashed rgba(0,0,0,0.08)'
                            }}>
                              "{ref.translation}"
                            </div>
                          )}
                        </div>
                      ));
                    }

                    // Fallback: render as content block if parsing failed
                  }

                  // M69: MAIN TEACHINGS - Parse and render subsections with distinct styling
                  if (section.type === 'main teachings') {
                    // Parse subsections from content items
                    // Pattern: Subsection headers are typically bold text followed by content
                    const parseTeachingSubsections = (items) => {
                      const subsections = [];
                      let currentSubsection = null;

                      items.forEach(item => {
                        const text = item.text?.trim() || '';

                        // Detect subsection header: shorter text (< 80 chars) that looks like a title
                        // Often has leading/trailing spaces from Google Docs formatting
                        const isLikelyHeader = text.length < 80 &&
                          text.length > 5 &&
                          !text.endsWith('.') &&
                          !text.includes('. ') && // Not a sentence
                          /^[A-Z]/.test(text.trim()); // Starts with capital letter

                        if (isLikelyHeader) {
                          // Save previous subsection if exists
                          if (currentSubsection) {
                            subsections.push(currentSubsection);
                          }
                          // Start new subsection
                          currentSubsection = {
                            title: text.trim(),
                            content: []
                          };
                        } else if (currentSubsection) {
                          // Add content to current subsection
                          currentSubsection.content.push(item);
                        } else {
                          // Content before any header - create untitled subsection
                          currentSubsection = {
                            title: null,
                            content: [item]
                          };
                        }
                      });

                      // Don't forget the last subsection
                      if (currentSubsection) {
                        subsections.push(currentSubsection);
                      }

                      return subsections;
                    };

                    const teachingSubsections = parseTeachingSubsections(section.content);

                    if (teachingSubsections.length > 0) {
                      return teachingSubsections.map((subsection, subIndex) => (
                        <div
                          key={subIndex}
                          className="dn-teaching-subsection"
                          style={{
                            background: cardBg,
                            borderRadius: '16px',
                            boxShadow: '0 3px 12px rgba(0, 0, 0, 0.05)',
                            border: '1px solid rgba(0, 0, 0, 0.04)',
                            overflow: 'hidden',
                            marginBottom: '16px',
                            animation: `dn-fadeInUp 0.5s ease-out ${0.1 * subIndex}s both`
                          }}
                        >
                          {/* Subsection header with accent */}
                          {subsection.title && (
                            <div style={{
                              padding: '16px 24px',
                              background: `linear-gradient(90deg, ${primaryColor}10 0%, transparent 100%)`,
                              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: primaryColor,
                                flexShrink: 0
                              }}></span>
                              <h4 style={{
                                margin: 0,
                                fontFamily: '"Cormorant Garamond", Georgia, serif',
                                fontSize: '1.15em',
                                fontWeight: 600,
                                color: primaryColor,
                                lineHeight: 1.3
                              }}>
                                {subsection.title}
                              </h4>
                            </div>
                          )}

                          {/* Subsection content */}
                          <div style={{
                            padding: '20px 24px',
                            lineHeight: 1.8,
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                            color: '#374151',
                            fontSize: '0.95em'
                          }}>
                            {subsection.content.map((item, itemIndex) => {
                              // PRINCIPLE: Preserve list structure when HTML contains <li> elements
                              // Otherwise bullet points are concatenated into a wall of text
                              const hasListItems = item.html && (item.html.includes('<li') || item.html.includes('<ul') || item.html.includes('<ol'));
                              if (hasListItems) {
                                return (
                                  <div
                                    key={itemIndex}
                                    className="dn-block-content"
                                    style={{ margin: itemIndex === 0 ? 0 : '12px 0 0 0' }}
                                    dangerouslySetInnerHTML={{ __html: item.html }}
                                  />
                                );
                              }
                              return (
                                <p key={itemIndex} style={{ margin: itemIndex === 0 ? 0 : '12px 0 0 0' }}>
                                  {item.text}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    }
                    // Fall through to default rendering if parsing fails
                  }

                  // ALL OTHER SECTIONS - Grouped content in one elegant card
                  return (
                    <div
                      className="dn-content-block"
                      style={{
                        background: cardBg,
                        borderRadius: '16px',
                        boxShadow: '0 3px 12px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.04)',
                        overflow: 'hidden',
                        animation: 'dn-fadeInUp 0.5s ease-out 0.1s both'
                      }}
                    >
                      {/* Accent bar at top */}
                      <div style={{
                        height: '4px',
                        background: `linear-gradient(90deg, ${section.color} 0%, ${accentGold} 100%)`
                      }}></div>

                      {/* Content area */}
                      <div
                        className="dn-block-content"
                        style={{
                          padding: '24px 28px',
                          lineHeight: 1.8,
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                          color: '#374151'
                        }}
                        dangerouslySetInnerHTML={{ __html: combinedHtml }}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        {/* Premium Styles */}
        <style dangerouslySetInnerHTML={{ __html: `
          /* Google Fonts import */
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@400;500;600&display=swap');

          /* Animation keyframes */
          @keyframes dn-fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Numbered card hover effects - KeyTakeaways style */
          .dn-numbered-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1) !important;
            border-color: ${accentGold}40 !important;
          }

          .dn-numbered-card:hover > div:first-child {
            background: linear-gradient(180deg, ${primaryColor}18 0%, ${primaryColor}08 100%) !important;
          }

          .dn-numbered-card:hover > div:last-child {
            background: linear-gradient(180deg, ${accentGold} 0%, ${accentGold}90 100%) !important;
          }

          /* Question card hover effects */
          .dn-question-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08) !important;
            border-color: rgba(0, 0, 0, 0.08) !important;
          }

          /* Quote card hover effects */
          .dn-quote-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08) !important;
          }

          /* Content block hover */
          .dn-content-block:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08) !important;
          }

          /* Bold title styling within content blocks */
          .dn-block-content strong,
          .dn-block-content b {
            display: inline;
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 1.05em;
            font-weight: 600;
            color: ${primaryColor};
          }

          /* Underlined terms (like Al-Kawthar, Abtar) */
          .dn-block-content u {
            text-decoration: none;
            font-weight: 600;
            color: ${primaryColor};
            border-bottom: 2px solid ${accentGold}50;
          }

          /* Block content paragraphs */
          .dn-block-content p {
            margin: 0 0 12px 0;
          }

          .dn-block-content p:last-child {
            margin-bottom: 0;
          }

          /* Lists in content blocks */
          .dn-block-content ul,
          .dn-block-content ol {
            margin: 12px 0;
            padding-left: 24px;
          }

          .dn-block-content li {
            margin: 8px 0;
            padding-left: 8px;
          }

          .dn-block-content li::marker {
            color: ${primaryColor};
          }

          /* Section content typography */
          .dn-section-content p {
            margin: 0;
          }

          .dn-section-content ul,
          .dn-section-content ol {
            margin: 8px 0;
            padding-left: 20px;
          }

          .dn-section-content li {
            margin: 6px 0;
            line-height: 1.75;
          }

          .dn-section-content a {
            color: ${primaryColor};
            text-decoration: none;
            border-bottom: 1px solid ${primaryColor}40;
            transition: all 0.2s ease;
          }

          .dn-section-content a:hover {
            border-color: ${primaryColor};
            background: ${primaryColor}08;
            padding: 0 4px;
            margin: 0 -4px;
            border-radius: 2px;
          }

          /* Responsive adjustments */
          @media (max-width: 640px) {
            .dn-section {
              margin-bottom: 24px !important;
            }

            .dn-numbered-card {
              flex-direction: column !important;
              border-radius: 14px !important;
            }

            .dn-numbered-card > div:first-child {
              min-width: unset !important;
              padding: 12px !important;
              border-right: none !important;
              border-bottom: 1px solid ${primaryColor}15 !important;
            }

            .dn-numbered-card > div:last-child {
              width: 100% !important;
              height: 4px !important;
            }

            .dn-question-card {
              padding: 16px 18px 16px 60px !important;
              border-radius: 12px !important;
            }

            .dn-question-card > span:first-child {
              width: 36px !important;
              height: 36px !important;
              font-size: 1.2em !important;
            }
          }

          /* Print styles */
          @media print {
            .dn-section {
              break-inside: avoid;
            }

            .dn-numbered-card,
            .dn-question-card,
            .dn-quote-card {
              box-shadow: none !important;
              border: 1px solid #e5e7eb !important;
            }
          }
        `}} />

        {/* M73: Floating Download PDF Button */}
        {url && extractGoogleDocId(url) && (
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            title={`Download as ${downloadFilename}`}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
              color: '#ffffff',
              borderRadius: '50px',
              border: 'none',
              cursor: isDownloading ? 'wait' : 'pointer',
              fontSize: '0.85em',
              fontWeight: 500,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              zIndex: 100,
              opacity: isDownloading ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isDownloading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>{isDownloading ? 'Downloading...' : 'Download PDF'}</span>
          </button>
        )}
      </div>
    );
  }

  // Raw content fallback - if we have content but couldn't parse it into sections
  if (content && !useIframeFallback) {
    return (
      <div className="detailed-notes-container" style={{
        padding: '0',
        background: `linear-gradient(180deg, ${lightBg} 0%, #ffffff 100%)`,
        minHeight: '100%'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          padding: '48px 24px 32px',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            gap: '16px'
          }}>
            <div style={{ width: '60px', height: '1px', background: `linear-gradient(90deg, transparent, ${accentGold})` }}></div>
            <div style={{ width: '8px', height: '8px', background: accentGold, transform: 'rotate(45deg)' }}></div>
            <div style={{ width: '60px', height: '1px', background: `linear-gradient(90deg, ${accentGold}, transparent)` }}></div>
          </div>

          <h2 style={{
            color: primaryColor,
            fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
            fontWeight: 600,
            fontFamily: 'Georgia, "Times New Roman", serif',
            letterSpacing: '-0.02em',
            margin: '0 0 12px 0'
          }}>
            Detailed Notes
          </h2>

          <p style={{
            color: '#6b7280',
            fontSize: '1.05em',
            fontStyle: 'italic',
            margin: 0,
            fontFamily: 'Georgia, "Times New Roman", serif'
          }}>
            Comprehensive lecture documentation
          </p>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '24px',
            gap: '8px'
          }}>
            <div style={{ width: '40px', height: '2px', background: primaryColor, borderRadius: '2px' }}></div>
            <div style={{ width: '6px', height: '6px', background: accentGold, borderRadius: '50%' }}></div>
            <div style={{ width: '40px', height: '2px', background: primaryColor, borderRadius: '2px' }}></div>
          </div>

        </div>

        {/* Raw Content */}
        <div
          className="dn-raw-content"
          dangerouslySetInnerHTML={{ __html: content }}
          style={{
            padding: '0 24px 48px',
            maxWidth: '880px',
            margin: '0 auto',
            lineHeight: 1.75,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            color: '#374151'
          }}
        />

        <style dangerouslySetInnerHTML={{ __html: `
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@400;500;600&display=swap');

          .dn-raw-content h1, .dn-raw-content h2, .dn-raw-content h3,
          .dn-raw-content .doc-heading-1, .dn-raw-content .doc-heading-2, .dn-raw-content .doc-heading-3 {
            font-family: 'Cormorant Garamond', Georgia, serif;
            color: ${primaryColor};
            margin: 24px 0 16px;
          }

          .dn-raw-content h2, .dn-raw-content .doc-heading-2 {
            font-size: 1.5em;
            padding-bottom: 8px;
            border-bottom: 2px solid ${primaryColor}20;
          }

          .dn-raw-content strong, .dn-raw-content b {
            color: ${primaryColor};
            font-weight: 600;
          }

          .dn-raw-content blockquote {
            margin: 16px 0;
            padding: 16px 20px;
            background: ${cardBg};
            border-left: 4px solid ${accentGold};
            border-radius: 8px;
            font-style: italic;
          }

          .dn-raw-content ul, .dn-raw-content ol {
            padding-left: 24px;
          }

          .dn-raw-content li {
            margin: 8px 0;
          }

          .dn-raw-content a {
            color: ${primaryColor};
            text-decoration: none;
            border-bottom: 1px solid ${primaryColor}40;
          }
        `}} />

        {/* M73: Floating Download PDF Button (fallback view) */}
        {url && extractGoogleDocId(url) && (
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            title={`Download as ${downloadFilename}`}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
              color: '#ffffff',
              borderRadius: '50px',
              border: 'none',
              cursor: isDownloading ? 'wait' : 'pointer',
              fontSize: '0.85em',
              fontWeight: 500,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              zIndex: 100,
              opacity: isDownloading ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isDownloading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>{isDownloading ? 'Downloading...' : 'Download PDF'}</span>
          </button>
        )}
      </div>
    );
  }

  // No content available
  return (
    <div style={{
      padding: '60px 24px',
      textAlign: 'center',
      background: `linear-gradient(135deg, ${lightBg} 0%, #ffffff 100%)`
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        margin: '0 auto 20px',
        background: lightBg,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '28px'
      }}>
        📝
      </div>
      <p style={{
        color: '#6b7280',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontStyle: 'italic',
        fontSize: '1.1em'
      }}>
        No detailed notes available for this lecture yet.
      </p>
    </div>
  );
};

export default DetailedNotes;
